import styled, { css } from 'styled-components';

import { responsive } from '@/utils';

import { cameraSelectorColors, cameraSelectorTransition } from './cameraSelector.tokens.ts';

const { penHeader, cardGrid, selectCard } = cameraSelectorColors;

/** Vertical stack of pen (parent) groups within a single site tab panel. */
export const CameraSiteContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${responsive.spacing.large};
  padding: 0 ${responsive.spacing.xsmall} ${responsive.spacing.medium};
`;

export const CameraPenHeader = styled.div`
  padding: ${responsive.spacing.small};
  /* Inherited by the parent-name Typography (rendered with color='inherit'). */
  color: ${penHeader.default};
  transition: ${cameraSelectorTransition};
`;

export const CameraCardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(11.5rem, 1fr));
  gap: ${responsive.spacing.medium};
  border: 1px solid ${cardGrid.border.default};

  padding: ${responsive.spacing.large} ${responsive.spacing['2xlarge']};
  border-radius: ${responsive.spacing.xlarge};
  transition: ${cameraSelectorTransition};
`;

/**
 * A pen: groups all camera cards that share a parent. On hover / keyboard or
 * gamepad focus, and when it holds the selected camera, the parent name turns
 * action-colored and the card grid gets an action-tinted background.
 */
export const CameraPenCard = styled.section<{ $selected: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${responsive.spacing.small};

  &:hover {
    ${CameraPenHeader} {
      color: ${penHeader.hover};
    }
    ${CameraCardGrid} {
      background: ${cardGrid.background.hover};
      border-color: ${cardGrid.border.hover};
    }
  }

  &:focus-within {
    ${CameraPenHeader} {
      color: ${penHeader.focus};
    }
    ${CameraCardGrid} {
      background: ${cardGrid.background.focus};
      border-color: ${cardGrid.border.focus};
    }
  }

  ${({ $selected }) =>
    $selected &&
    css`
      ${CameraPenHeader},
      &:hover ${CameraPenHeader},
      &:focus-within ${CameraPenHeader} {
        color: ${penHeader.selected};
      }

      ${CameraCardGrid},
      &:hover ${CameraCardGrid},
      &:focus-within ${CameraCardGrid} {
        background: ${cardGrid.background.selected};
        border: 1px solid ${cardGrid.border.selected};
      }
    `}
`;

/**
 * A camera card. Default: dark surface with a subtle border. Selected: solid
 * action-blue fill with on-solid text (the chosen camera). Hover: subtle blue
 * lift. Focus (keyboard / gamepad): blue ring so the navigated card stands out.
 */
export const CameraSelectCard = styled.button<{ $selected: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${responsive.spacing['large']};
  width: 100%;
  padding: ${responsive.spacing.xlarge};
  text-align: left;
  cursor: pointer;
  border-radius: ${responsive.spacing.xlarge};
  border: 1px solid
    ${({ $selected }) => ($selected ? selectCard.border.selected : selectCard.border.default)};
  background: ${({ $selected }) =>
    $selected ? selectCard.background.selected : selectCard.background.default};
  color: ${({ $selected }) => ($selected ? selectCard.color.selected : selectCard.color.default)};
  transition: ${cameraSelectorTransition};

  &:hover {
    border-color: ${({ $selected }) =>
      $selected ? selectCard.border.hover.selected : selectCard.border.hover.default};
    background: ${({ $selected }) =>
      $selected ? selectCard.background.hover.selected : selectCard.background.hover.default};
  }

  &:focus-visible,
  &:focus {
    outline: none;
    border-color: ${selectCard.border.focus};
    box-shadow: 0 0 0 2px ${selectCard.focusRing};
  }
`;
